import { memo, useCallback, useMemo } from "react";

import { isString } from "lodash";

import { Box, useTheme } from "@mui/material";
import { NavLink, Route, Routes, useLocation } from "react-router-dom";
import Screen from "../../components/layout/Screen";
import TitledHeader from "../../components/layout/TitledHeader";
import UploadedSizersRoute from "./routes/UploadedSizersRoute/UploadedSizersRoute";
import ExceptionsRoute from "./routes/ExceptionsRoute/ExceptionsRoute";
import OpenTasksRoute from "./routes/OpenTasksRoute/OpenTasksRoute";

const GlobalProcessingScreen = () => {
  const tabs = useMemo(() => ["Uploaded Sizers"], []);
  // const tabs = useMemo(() => ["Exceptions", "Uploaded Sizers", "Tasks"], []);
  return (
    <Screen>
      <TitledHeader title={"Processing"} sx={{ pb: 0 }}>
        <RouterTabSwitcher
          sx={{ ml: -0.8 }}
          useIndex
          baseUrl="/processing/"
          variant={"underline"}
          tabs={tabs}
        />
      </TitledHeader>
      <ContentRouter />
    </Screen>
  );
};

const ContentRouter = memo(({ baseUrl }) => {
  return (
    <Box
      sx={{
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Routes>
        <Route path="/" element={<UploadedSizersRoute />} />
        <Route path="/exceptions" element={<ExceptionsRoute />} />
        <Route path="/uploaded-sizers" element={<UploadedSizersRoute />} />
        <Route path="/tasks" element={<OpenTasksRoute />} />

        {/* <Route path="*" element={<Navigate to={`${baseUrl}`} replace />} /> */}
      </Routes>
    </Box>
  );
});

const RouterTabSwitcher = ({
  tabs,
  baseUrl,
  orientation = "horizontal",
  variant = "background",
  onChange,
  underlinePosition = "bottom",
  sx,
}) => {
  const location = useLocation();

  const currentPath = useMemo(
    () => location.pathname.replace(/\/$/, ""),
    [location.pathname]
  );

  const cleanBase = useMemo(() => baseUrl.replace(/\/$/, ""), [baseUrl]);

  const styles = useMemo(() => {
    return {
      display: "flex",
      flexDirection: orientation === "horizontal" ? "row" : "column",
      ".nav-link": {
        fontFamily: "var(--ibm)",
        color: "#181d1f",
        fontWeight: 400,
        fontSize: "13px",
        p: 1.5,
        borderRadius: 0,
        whiteSpace: "nowrap",
        textDecoration: "none",
        transition: "all 0.2s ease",

        // Prevent text wrapping in vertical orientation
        ...(orientation === "vertical" && {
          whiteSpace: "nowrap",
          minWidth: "fit-content",
          justifyContent: "flex-start",
          textAlign: "left",
          borderLeft: "2px solid transparent",
        }),

        "&.active": {
          color: "#2962ff",
          fontWeight: 700,
          borderBottom: "2px solid #2962ff",

          // Override default background for variants
          ...(variant === "underline"
            ? {
                background: "none",
                borderRadius: "0 !important",
                fontWeight: 500,
              }
            : {
                background: "#2962ff14",
              }),

          // Handle border placement based on orientation and position
          ...(variant === "underline" && {
            ...(orientation === "horizontal" &&
              underlinePosition === "bottom" && {
                borderBottom: "2px solid #2962ff",
              }),
            ...(orientation === "horizontal" &&
              underlinePosition === "top" && {
                borderTop: "2px solid #2962ff",
              }),
            ...(orientation === "vertical" &&
              underlinePosition === "left" && {
                borderLeft: "2px solid #2962ff",
              }),
            ...(orientation === "vertical" &&
              underlinePosition === "right" && {
                borderRight: "2px solid #2962ff",
              }),
          }),
        },
      },
      ...sx,
    };
  }, [orientation, variant, underlinePosition, sx]);

  // Helper function to determine if a tab should be active
  const isTabActive = useCallback(
    (tabKey, linkActive) => {
      const isSummary = tabKey === "summary";

      if (isSummary) {
        return [cleanBase, `${cleanBase}/summary`].includes(currentPath);
      }

      // For borrowers and properties tabs, check if current path starts with the tab path
      if (tabKey === "borrowers") {
        return currentPath.includes(`${cleanBase}/borrowers`);
      }

      if (tabKey === "properties") {
        return currentPath.includes(`${cleanBase}/properties`);
      }

      // For other tabs, use the default NavLink active state
      return linkActive;
    },
    [cleanBase, currentPath]
  );

  const tabItems = useMemo(() => {
    return tabs.map((tab) => {
      const tabKey = isString(tab)
        ? tab.toLowerCase().replace(/\s+/g, "-")
        : tab.path;
      const label = isString(tab) ? tab : tab.label;

      const isSummary = tabKey === "summary";
      // summary links to the bare base, others to base/<tabKey>
      const to = isSummary ? cleanBase : `${cleanBase}/${tabKey}`;

      return {
        tabKey,
        label,
        to,
        isSummary,
      };
    });
  }, [tabs, cleanBase]);

  return (
    <Box sx={styles}>
      {tabItems.map(({ tabKey, label, to, isSummary }) => (
        <NavLink
          key={tabKey}
          to={to}
          onClick={() => {
            if (onChange) {
              onChange(tabKey);
            }
          }}
          // only force NavLink to do exact matching on the non-Summary tabs
          end={!isSummary}
          className={({ isActive: linkActive }) => {
            const manualActive = isTabActive(tabKey, linkActive);
            return `nav-link${manualActive ? " active" : ""}`;
          }}
        >
          {label}
        </NavLink>
      ))}
    </Box>
  );
};

export default GlobalProcessingScreen;
